import React from 'react';
import { Formatting } from '../utils';
import styled from 'styled-components';
import moment from 'moment';
import {
	Client,
	ClientOrder,
	ClientOrderPaymentState,
	getClientOrderColor,
	getReadableBdo,
	getReadablePaymentType,
	getReadablePriceType,
	PriceType,
} from 'types';

const PrintTable = styled.table`
	width: 100%;
	page-break-inside: auto;

	tr {
		page-break-inside: avoid;
		page-break-after: auto;

		&:nth-child(2n) {
			background: #eee;
		}

		> td,
		> th {
			padding: 4px 8px;
			text-align: center;
		}

		> th {
			font-size: 14px;

			> div {
				display: flex;
				flex-direction: column;

				> h5 {
					font-weight: 600;
					margin-bottom: 4px;
				}

				> span {
					font-weight: 400;
					font-size: 8px;
				}
			}
		}

		> td {
			font-size: 10px;

			> div {
				display: flex;
				flex-direction: column;

				> h6 {
					font-weight: 600;
					font-size: 12px;
					margin-bottom: 2px;
				}
			}
		}
	}
`;

const PrintTitle = styled.h2`
	width: 100%;
	margin-top: 16px;
	margin-bottom: 8px;
	text-align: center;
	font-size: 18px;
`;

const PrintSubtitle = styled.h3`
	width: 100%;
	margin-top: 16px;
	margin-bottom: 32px;
	text-align: center;
	font-size: 14px;
`;

const DayTitle = styled(PrintSubtitle)`
	margin-top: 48px;
`;

const Summary = styled.h4`
	text-align: center;
	margin: 0;
	font-weight: 400;

	&:first-of-type {
		margin-top: 32px;
	}
`;

interface Props {
	client: Client;
	orders: ClientOrder[];
}

const ClientOrdersPrint: React.FC<Props> = ({ client, orders }) => {
	if (!orders) return <div></div>;

	const days = orders.reduce<Record<string, ClientOrder[]>>((acc, order) => {
		const date = order.date;
		const dateString = moment(date).format('DD.MM.YYYY');

		if (!acc[dateString]) acc[dateString] = [];
		acc[dateString].push(order);

		return acc;
	}, {});

	const totalSum = orders.reduce((acc, order) => acc + order.grossPrice, 0);
	const unpaidSum = orders.reduce((acc, order) => {
		if (order.paymentState !== ClientOrderPaymentState.unpaid) {
			return acc;
		}

		const leftToPay = order.grossPrice - order.alreadyPaid;
		return acc + Math.max(0, leftToPay);
	}, 0);
	const overdueSum = orders.reduce((acc, order) => {
		if (order.paymentState !== ClientOrderPaymentState.overdue) {
			return acc;
		}

		const leftToPay = order.grossPrice - order.alreadyPaid;
		return acc + Math.max(0, leftToPay);
	}, 0);

	let k = 0;

	return (
		<div key={client.id} className="print">
			<PrintTitle>
				Wydruk zamówień dla klienta {client.name}
				{client.street && client.city && client.postal && ` - ${client.street}, ${client.city} ${client.postal}`}
			</PrintTitle>
			<PrintSubtitle>Stan z dnia {Formatting.epochToDate(new Date())}</PrintSubtitle>
			{Object.entries(days).map(([day, orders]) => (
				<React.Fragment key={day}>
					<DayTitle>{day}</DayTitle>
					<PrintTable>
						<tbody>
							<tr>
								<th>
									<div>
										<h5>LP</h5>
										<span>w grupie</span>
									</div>
								</th>
								<th>
									<div>
										<h5>Klient</h5>
										<span>Nazwa</span>
										<span>NIP</span>
										<span>Email</span>
									</div>
								</th>
								<th>
									<div>
										<h5>Cena</h5>
										<span>Brutto / Netto</span>
										<span>Typ płatności</span>
									</div>
								</th>
								<th>
									<div>
										<h5>Adres</h5>
										<span>BDO</span>
										<span>Rodzaj odpadów</span>
									</div>
								</th>
								<th>Faktura</th>
								<th>Data</th>
							</tr>
							{orders.map((order, j) => (
								<tr key={order.id}>
									<td
										style={{
											background: getClientOrderColor(order),
											minWidth: '100px',
										}}
									>
										<div>
											<h6>{++k}</h6>
											<span>{j + 1}</span>
										</div>
									</td>
									<td>
										<div>
											<span>{client.name}</span>
											<span>{client.nip}</span>
											<span>{client.email}</span>
										</div>
									</td>
									<td>
										<div>
											<h6>{Formatting.money(order.priceType === PriceType.net ? order.netPrice : order.grossPrice)}</h6>
											<span>{getReadablePriceType(order.priceType)}</span>
											<span>{getReadablePaymentType(order.wantedPaymentType)}</span>
										</div>
									</td>
									<td>
										<div>
											<h6>{order.address}</h6>
											<span>{getReadableBdo(order.bdo)}</span>
											<span>{order.wasteType}</span>
										</div>
									</td>
									<td>{order.invoiceDate ?? '-'}</td>
									<td>{Formatting.epochToDate(order.date, true)}</td>
								</tr>
							))}
						</tbody>
					</PrintTable>
				</React.Fragment>
			))}
			<Summary>Nieopłacone (minus zaliczki): {Formatting.money(unpaidSum)}</Summary>
			<Summary>Zaległe (minus zaliczki): {Formatting.money(overdueSum)}</Summary>
			<Summary>Łączna kwota zamówień brutto: {Formatting.money(totalSum)}</Summary>
		</div>
	);
};

export default class extends React.PureComponent<Props> {
	override render() {
		return <ClientOrdersPrint {...this.props} />;
	}
}
